import { useState } from "react";
import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import SplitPaymentUnavailable from "@/shared/ui/components/split-payment-unavailable";
import { CancelIcon } from "@/shared/ui/icons/cancel-icon";
import { BuyerProtectionModal } from "@/modules/plan-selection/components/buyer-protection-modal";
import {
  UnequalPlanBanner,
  UnequalPlanBannerWithFee,
} from "@/modules/plan-selection/components/instalment-option-picker";
import { formatWithComponents } from "@/lib/common";
import { LOCALIZED_ICON_TYPE, type AppContext, type PaymentOption } from "@/types";
import * as events from "@/events";
import { PlanAmountBreakdown } from "./plan-amount-breakdown";
import { PlanDetailsSheet } from "./plan-details-sheet";
import { resolvePlanAmountBreakdown } from "../lib/resolve-plan-amount-breakdown";
import { resolvePlanFeeLine } from "../lib/resolve-plan-fee-line";

interface PreSelectedPlanSummaryProps {
  ctx: AppContext;
  option: PaymentOption;
  /** Mirrors PaymentMethodsView wallet toggle — breakdown rows only when ON. */
  isWalletSelected?: boolean;
}

export function PreSelectedPlanSummary({
  ctx,
  option,
  isWalletSelected = false,
}: PreSelectedPlanSummaryProps) {
  const { t, i18n } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [buyerProtectionOpen, setBuyerProtectionOpen] = useState(false);
  const { session } = ctx;
  const currency = session.order.currency;
  const isPayInFull = option.tenure === 0;
  const isInstalments = option.tenure > 1;
  const monthly = option.instalmentAmount;
  const hasAdjustedDownPayment = option.equalDownPaymentStrategy === false;
  const downPaymentAmount = option.downPaymentAmount.amount;
  const hasIncludesFees = (option.totalCustomerFeeAmount?.amount ?? 0) > 0;
  const feeLine = resolvePlanFeeLine(option);
  const breakdown = resolvePlanAmountBreakdown(session, option, isWalletSelected);
  const todayKey = "plan_page_improvements.common.today";
  const thenKey = "plan_page_improvements.common.then";
  const perMonthKey = "plan_page_improvements.common.per_month";
  const includesFeeKey =
    (option.averageMonthlyFee?.amount ?? 0) > 0
      ? "plan.includes_monthly_fee"
      : "plan.includes_fee";

  if (isPayInFull) {
    return (
      <div className="px-6 pt-4 pb-6" data-testid="single-page-top-slot">
        <h1 className="mb-3 text-[22px] font-semibold text-gray-900">
          {t("plan.pay_in_full")}
        </h1>
        <div className="rounded-2xl bg-[#F5F6FA] p-4">
          <div className="text-[18px] font-semibold text-gray-900">
            <span className="mr-1 rtl:mr-0 rtl:ml-1">
              {t("plan.pay_today_prefix")}
            </span>
            <MoneyAmount
              amount={option.downPaymentAmount.amount}
              currency={currency}
              language={i18n.language}
              className="inline-flex mx-1"
            />
            <span className="ml-1 rtl:ml-0 rtl:mr-1">
              {t("plan.pay_today_suffix")}
            </span>
          </div>
          <button
            type="button"
            data-testid="single-page-buyer-protection"
            className="mt-1 text-[14px] text-[#585B7A] underline transition-colors hover:text-purple-600 focus:outline-hidden focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={() => {
              ctx.sendEvent(events.BUYER_PROTECTION_LABEL_CLICKED, {
                payment_type: "PAY_NOW",
                source: "single_page_checkout",
              });
              setBuyerProtectionOpen(true);
            }}
          >
            {t("plan.full_buyer_protection")}
          </button>
        </div>
        <div className="mt-4">
          <SplitPaymentUnavailable />
        </div>
        <BuyerProtectionModal
          isOpen={buyerProtectionOpen}
          isClosing={false}
          onClose={() => setBuyerProtectionOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="px-6 pt-4 pb-6" data-testid="single-page-top-slot">
      <h1 className="mb-2 text-[22px] font-semibold text-gray-900">
        {t("single_page_checkout.split_title", { number: option.tenure })}
      </h1>
      <div
        className="mb-4 flex items-center gap-2 text-[14px] text-[#585B7A]"
        data-testid="single-page-cancel-message"
      >
        <CancelIcon />
        <span>{t("payment.cancel_message")}</span>
      </div>
      <div className="rounded-2xl bg-[#F5F6FA] p-4">
        <div className="flex items-center justify-between">
          <div
            className="text-[18px] font-semibold text-gray-900"
            data-testid={
              hasAdjustedDownPayment
                ? "single-page-unequal-amounts"
                : "single-page-equal-amounts"
            }
          >
            {hasAdjustedDownPayment ? (
              <>
                <MoneyAmount
                  amount={downPaymentAmount}
                  currency={currency}
                  language={i18n.language}
                  className="inline-flex"
                />
                <span className="font-semibold"> {t(todayKey)}</span>
                <div className="text-[16px] font-medium text-[#585B7A] mt-1">
                  {t(thenKey)}{" "}
                  <MoneyAmount
                    amount={monthly.amount}
                    currency={currency}
                    language={i18n.language}
                    className="inline-flex"
                  />
                  {t(perMonthKey)}
                </div>
              </>
            ) : (
              <>
                <MoneyAmount
                  amount={monthly.amount}
                  currency={currency}
                  language={i18n.language}
                  className="inline-flex"
                />
                {isInstalments && t("single_page_checkout.per_month")}
              </>
            )}
          </div>
          <button
            type="button"
            data-testid="single-page-see-details"
            aria-haspopup="dialog"
            aria-expanded={detailsOpen}
            className="flex shrink-0 items-center gap-1 rounded-full bg-purple-600 px-3 py-1 text-[12px] font-bold text-white transition-colors hover:bg-purple-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={() => {
              ctx.sendEvent(events.SEE_PAYMENT_PLAN_DETAILS_CLICKED, {
                source: "single_page_checkout",
              });
              setDetailsOpen(true);
            }}
          >
            <span>{t("single_page_checkout.more_details")}</span>
            <span
              aria-hidden
              className="inline-flex [&_path]:fill-white [&_path]:stroke-white"
            >
              {ctx.localizedIcon(LOCALIZED_ICON_TYPE.CHEVRON_FORWARD)}
            </span>
          </button>
        </div>
        {!hasAdjustedDownPayment && feeLine.kind === "includes" && (
          <p
            className="mt-1 text-[13px] text-[#585B7A]"
            data-testid="single-page-fee-line"
          >
            {formatWithComponents(
              t(includesFeeKey),
              {
                amount: (
                  <MoneyAmount
                    amount={feeLine.amount}
                    currency={feeLine.currency}
                    language={i18n.language}
                    className="inline-flex mx-0.5"
                  />
                ),
              },
              (text) => (
                <span className="mr-0.5 rtl:mr-0 rtl:ml-0.5">{text}</span>
              ),
            )}
          </p>
        )}
        {!hasAdjustedDownPayment && feeLine.kind === "no_fees" && (
          <p
            className="mt-1 text-[13px] text-[#128351]"
            data-testid="single-page-no-fees"
          >
            {t("plan_page_improvements.plan.no_fees")}
          </p>
        )}
        {hasAdjustedDownPayment && (
          <>
            {hasIncludesFees ? (
              <UnequalPlanBannerWithFee
                downPaymentAmount={downPaymentAmount}
                currency={currency}
                language={i18n.language}
                t={t}
                averageMonthlyFee={option.averageMonthlyFee}
                containerClassName="mt-3 mb-0"
              />
            ) : (
              <UnequalPlanBanner
                downPaymentAmount={downPaymentAmount}
                currency={currency}
                language={i18n.language}
                t={t}
                containerClassName="mt-3 mb-0"
              />
            )}
          </>
        )}
        <PlanAmountBreakdown
          walletDiscount={breakdown.walletDiscount}
          dueToday={breakdown.dueToday}
        />
      </div>
      <PlanDetailsSheet
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        ctx={ctx}
        option={option}
      />
    </div>
  );
}
